/**
 * PositiveAccountWarning Component
 * 
 * Warning modal shown when a user attempts to dispute a current/positive account
 * that has no strong dispute signals. Allows user to proceed anyway with acknowledgment.
 */

import React from 'react';
import {
  AlertTriangle,
  CheckCircle,
  X,
  AlertCircle,
  Info
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';

// Valid reasons a user might have for disputing a positive account
const VALID_DISPUTE_REASONS = [
  { icon: AlertCircle, text: 'Account details are inaccurate' },
  { icon: AlertCircle, text: 'Balance is wrong' },
  { icon: AlertCircle, text: 'Dates are wrong' },
  { icon: AlertCircle, text: 'Remarks are incorrect' },
  { icon: AlertCircle, text: 'Payment history is inaccurate' },
  { icon: AlertCircle, text: 'Account is not mine' },
];

export const PositiveAccountWarning = ({ 
  open, 
  onClose, 
  onProceed,
  accountName 
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-lg" data-testid="positive-account-warning-modal">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={24} className="text-amber-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-stone-900">
              This Account Appears Current & Positive
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 mt-4">
              {/* Warning Message */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  Our review does not currently detect a strong dispute signal on this account.
                  Disputing a current positive account may be unnecessary and could affect a
                  tradeline you may want to preserve.
                </p>
                <p className="text-sm text-amber-800 mt-2 font-medium">
                  Only continue if you believe the account contains inaccurate information.
                </p>
              </div>
              
              {/* Account Reference */}
              {accountName && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <Info size={16} className="text-slate-400" />
                  <span className="text-sm text-slate-600">
                    Account: <span className="font-medium text-stone-800">{accountName}</span>
                  </span>
                </div>
              )}
              
              {/* Valid Reasons Section */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">
                  Valid reasons to dispute a positive account:
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {VALID_DISPUTE_REASONS.map((reason, idx) => {
                    const Icon = reason.icon;
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm text-slate-600"
                      >
                        <Icon size={14} className="text-slate-400 flex-shrink-0" />
                        <span>{reason.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter className="mt-6 flex-col sm:flex-row gap-2">
          <AlertDialogCancel asChild>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full sm:w-auto"
              data-testid="caution-cancel-btn"
            >
              Cancel
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button 
              onClick={onProceed}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="caution-proceed-btn"
            >
              <AlertTriangle size={16} className="mr-2" />
              Proceed Anyway
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PositiveAccountWarning;
